import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface VoiceSettings {
  id: number;
  name: string;
  description?: string;
  tone: string;
  perspective: string;
  style: string;
  avoid_terms: string[];
  include_elements: string[];
  preferred_length: string;
  cta_style: string;
  is_default: boolean;
  created_at: string;
  updated_at?: string;
}

export interface CreateVoiceSettingsRequest {
  name: string;
  description?: string;
  tone: string;
  perspective: string;
  style: string;
  avoid_terms: string[];
  include_elements: string[];
  preferred_length: string;
  cta_style: string;
  is_default?: boolean;
}

export interface UpdateVoiceSettingsRequest extends Partial<CreateVoiceSettingsRequest> {
  // All fields are optional for updates
}

class VoiceSettingsService {
  /**
   * Get all voice settings
   */
  async getVoiceSettings(): Promise<VoiceSettings[]> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/voice-settings`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch voice settings: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a specific voice setting by ID
   */
  async getVoiceSetting(id: number): Promise<VoiceSettings> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/voice-settings/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch voice setting: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get the default voice setting
   */
  async getDefaultVoiceSetting(): Promise<VoiceSettings> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/voice-settings/default`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch default voice setting: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create a new voice setting
   */
  async createVoiceSetting(data: CreateVoiceSettingsRequest): Promise<VoiceSettings> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/voice-settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to create voice setting: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update an existing voice setting
   */
  async updateVoiceSetting(id: number, data: UpdateVoiceSettingsRequest): Promise<VoiceSettings> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/voice-settings/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update voice setting: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a voice setting
   */
  async deleteVoiceSetting(id: number): Promise<void> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/voice-settings/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to delete voice setting: ${response.statusText}`);
    }
  }

  /**
   * Set a voice setting as the default
   */
  async setDefaultVoiceSetting(id: number): Promise<VoiceSettings> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/voice-settings/${id}/set-default`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to set default voice setting: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Validate voice settings data before submission
   */
  validateVoiceSettings(data: CreateVoiceSettingsRequest | UpdateVoiceSettingsRequest): string[] {
    const errors: string[] = [];

    if ('name' in data && (!data.name || data.name.trim().length === 0)) {
      errors.push('Name is required');
    }

    if ('tone' in data && (!data.tone || data.tone.trim().length === 0)) {
      errors.push('Tone is required');
    }

    if ('perspective' in data && (!data.perspective || data.perspective.trim().length === 0)) {
      errors.push('Perspective is required');
    }

    if ('style' in data && (!data.style || data.style.trim().length === 0)) {
      errors.push('Style is required');
    }

    if ('preferred_length' in data && (!data.preferred_length || data.preferred_length.trim().length === 0)) {
      errors.push('Preferred length is required');
    }

    if ('cta_style' in data && (!data.cta_style || data.cta_style.trim().length === 0)) {
      errors.push('CTA style is required');
    }

    if ('avoid_terms' in data && data.avoid_terms && !Array.isArray(data.avoid_terms)) {
      errors.push('Avoid terms must be an array');
    }

    if ('include_elements' in data && data.include_elements && !Array.isArray(data.include_elements)) {
      errors.push('Include elements must be an array');
    }

    return errors;
  }

  /**
   * Create default voice settings data
   */
  getDefaultFormData(): CreateVoiceSettingsRequest {
    return {
      name: '',
      description: '',
      tone: 'warm, supportive, professional',
      perspective: 'understanding parent community',
      style: 'conversational but authoritative',
      avoid_terms: ['medical advice', 'overwhelming jargon', 'guarantees'],
      include_elements: ['practical tips', 'community support', 'next steps'],
      preferred_length: '300-500 words',
      cta_style: 'gentle encouragement',
      is_default: false
    };
  }

  /**
   * Get preset voice setting templates
   */
  getPresetTemplates(): Omit<CreateVoiceSettingsRequest, 'name'>[] {
    return [
      {
        description: 'Professional and authoritative tone for formal communications',
        tone: 'professional, authoritative, confident',
        perspective: 'expert educational institution',
        style: 'formal but approachable',
        avoid_terms: ['casual language', 'slang', 'emotional appeals'],
        include_elements: ['credentials', 'facts', 'clear next steps'],
        preferred_length: '400-600 words',
        cta_style: 'direct and clear',
        is_default: false
      },
      {
        description: 'Warm and empathetic tone for sensitive topics',
        tone: 'empathetic, understanding, gentle',
        perspective: 'caring parent community',
        style: 'conversational and supportive',
        avoid_terms: ['clinical terms', 'judgmental language', 'pressure'],
        include_elements: ['emotional support', 'shared experiences', 'hope'],
        preferred_length: '250-400 words',
        cta_style: 'encouraging invitation',
        is_default: false
      },
      {
        description: 'Urgent but supportive tone for time-sensitive communications',
        tone: 'urgent, supportive, action-oriented',
        perspective: 'experienced educational advocates',
        style: 'direct but caring',
        avoid_terms: ['panic language', 'ultimatums', 'fear tactics'],
        include_elements: ['immediate benefits', 'limited availability', 'quick wins'],
        preferred_length: '200-350 words',
        cta_style: 'clear time-sensitive action',
        is_default: false
      }
    ];
  }
}

export const voiceSettingsService = new VoiceSettingsService();